import { DashboardEvent, EventsMeta, isDashboardEvent } from '../types/events.js';
import { API_BASE, FRESH_DATA_WINDOW_MS, LIVE_DELAY_MS, LIVE_POLL_MS } from './config.js';
import { authFetch } from './auth.js';
import { state } from './state.js';
import { addDaysJst, jst5amMs, jstDateStr, todayJstDateStr } from './utils.js';

export type EngineMode = 'idle' | 'playback' | 'live';

export interface EngineOptions {
  readonly bucketVirtualMs: number;
  readonly dispatch: (event: DashboardEvent, silent: boolean) => void;
  /** seek / loadDay 開始時に state リセット用に呼ばれる */
  readonly onSeek: () => void;
  /** silent dispatch (= 過去分の一気投入) の前に呼ばれる。DOM 更新を抑制するモードに入るのに使う。 */
  readonly onSilentBegin: () => void;
  /** silent dispatch 完了時に呼ばれる。抑制したぶんを最終 1 回 render するのに使う。 */
  readonly onSilentEnd: () => void;
  /**
   * ある区間のイベントを取り終えた時に呼ばれる (同じ区間のコストを取るのに使う)。
   * 取り終えてから呼ぶのは、コスト側の DB スナップショットをイベント側より後にして、
   * 取れたイベントのコストが必ず見えるようにするため。
   */
  readonly onRangeFetched?: (fromMs: number, toMs: number) => void;
  /** mode が変わった時に呼ばれる (UI ステータス更新用)。 */
  readonly onModeChange?: (mode: EngineMode) => void;
}

function parseMeta(value: unknown): EventsMeta {
  const meta: EventsMeta = { count: 0, firstAt: null, lastAt: null };
  if (value === null || typeof value !== 'object') return meta;
  const obj: { [k: string]: unknown } = Object.fromEntries(Object.entries(value));
  const count = obj['count'];
  if (typeof count === 'number') {
    Object.assign(meta, { count });
  }
  const firstAt = obj['firstAt'];
  if (typeof firstAt === 'string' || firstAt === null) {
    Object.assign(meta, { firstAt });
  }
  const lastAt = obj['lastAt'];
  if (typeof lastAt === 'string' || lastAt === null) {
    Object.assign(meta, { lastAt });
  }
  return meta;
}

export class PlaybackEngine {
  private mode: EngineMode = 'idle';
  private cursorMs: number | null = null;
  private speed = 0;
  private buffer: DashboardEvent[] = [];
  private fetchedUpto: number | null = null;
  private lastTickWallMs: number | null = null;
  private firstAt: number | null = null;
  private lastAt: number | null = null;
  /** 現在ロードしている JST 暦日 (YYYY-MM-DD)。loadDay 時にセット。 */
  private loadedDay: string | null = null;
  private fetchInFlight = false;
  private livePollHandle: number | null = null;

  constructor(private readonly options: EngineOptions) {
    window.setInterval(() => this.tick(), 50);
  }

  isActive(): boolean {
    return this.mode === 'live' || (this.mode === 'playback' && this.speed !== 0);
  }

  isLive(): boolean {
    return this.mode === 'live';
  }

  getMode(): EngineMode {
    return this.mode;
  }

  getSpeed(): number {
    return this.mode === 'live' ? 1 : this.speed;
  }

  getCursorMs(): number | null {
    return this.cursorMs;
  }

  getFirstAtMs(): number | null {
    return this.firstAt;
  }

  getLastAtMs(): number | null {
    return this.lastAt;
  }

  getLoadedDay(): string | null {
    return this.loadedDay;
  }

  /** ロード済みの先端 (fetchAhead が取り終えた最新の to)。シークバーの buffered 表示用。 */
  getFetchedUptoMs(): number | null {
    return this.fetchedUpto;
  }

  /** 起動時の自動初期化。meta を見て今日 live or 過去再生を選択。 */
  async init(): Promise<void> {
    const meta = await this.fetchMeta();
    const lastAtMs = meta.lastAt === null ? null : new Date(meta.lastAt).getTime();
    const firstAtMs = meta.firstAt === null ? null : new Date(meta.firstAt).getTime();

    const fresh = lastAtMs !== null && lastAtMs >= Date.now() - FRESH_DATA_WINDOW_MS;
    if (fresh) {
      await this.loadDay(todayJstDateStr(), { autoLive: true });
      return;
    }
    if (firstAtMs !== null) {
      // 過去スナップショット (events.json など)。データがある日を 60x で再生。
      await this.loadDay(jstDateStr(firstAtMs), { autoLive: false, autoPlaySpeed: 60 });
      return;
    }
    // データなし: 今日を pause で読み込む
    await this.loadDay(todayJstDateStr(), { autoLive: false });
  }

  private async fetchMeta(): Promise<EventsMeta> {
    try {
      const res = await authFetch(`${API_BASE}/events/meta`);
      const raw: unknown = await res.json();
      return parseMeta(raw);
    } catch {
      return { count: 0, firstAt: null, lastAt: null };
    }
  }

  /**
   * 指定の JST 日 (YYYY-MM-DD) を [日 05:00, 翌 05:00) でロードする。
   * - autoLive=true & 今日かつ live edge が範囲内: live モードに直接突入 (cursor=now-DELAY)
   * - autoPlaySpeed が指定されていれば、その speed で playback 再開
   * - そうでなければ pause (cursor=日先頭)
   */
  async loadDay(
    yyyymmdd: string,
    opts: { autoLive: boolean; autoPlaySpeed?: number },
  ): Promise<void> {
    const start = jst5amMs(yyyymmdd);
    const end = jst5amMs(addDaysJst(yyyymmdd, 1));

    this.stopLivePoll();
    this.options.onSeek();
    this.buffer = [];
    this.fetchedUpto = null;
    this.firstAt = start;
    this.lastAt = end;
    this.loadedDay = yyyymmdd;
    this.cursorMs = start;
    this.lastTickWallMs = performance.now();
    this.speed = 0;
    state.engineSpeed = null;
    this.mode = 'playback';
    this.notifyModeChange();

    if (opts.autoLive && this.canGoLiveNow()) {
      await this.enterLive();
      return;
    }

    // 1 日分をまとめて先取り (~6k 件 / 数 MB 程度を許容)。
    // 過去日は cursor を最初のイベント直前にジャンプして、無音時間を待たずに再生開始する。
    const fetchEnd = Math.min(end, Date.now());
    if (fetchEnd > start) {
      await this.fetchAndAppend(start, fetchEnd);
      this.fetchedUpto = fetchEnd;
      const head = this.buffer[0];
      if (head) {
        const headMs = new Date(head.timestamp).getTime();
        this.cursorMs = Math.max(start, headMs - 1_000);
      }
    }

    if (opts.autoPlaySpeed !== undefined && opts.autoPlaySpeed > 0) {
      this.setSpeed(opts.autoPlaySpeed);
    }
  }

  /** 現時点で live edge (now - DELAY) がロード済み日範囲内かどうか。 */
  canGoLiveNow(): boolean {
    if (this.firstAt === null || this.lastAt === null) return false;
    const liveEdge = Date.now() - LIVE_DELAY_MS;
    return liveEdge >= this.firstAt && liveEdge <= this.lastAt;
  }

  /** live モードに移行 (cursor を live edge にシークしてから pin)。 */
  async enterLive(): Promise<void> {
    if (!this.canGoLiveNow()) return;
    const liveEdge = Date.now() - LIVE_DELAY_MS;
    // 既存 seek で「日先頭〜live edge」を silent dispatch。
    // seek 内部の fetchAhead は cursor + 60 min 先まで取りに行ってしまうので、
    // 実際にイベントが存在する Date.now() までを buffer に持ったあと、fetchedUpto を
    // ここに合わせる (こうしないと次の poll が「無効な未来範囲」を取りに行く)。
    await this.seek(liveEdge);
    this.fetchedUpto = Date.now();
    this.mode = 'live';
    this.speed = 1;
    state.engineSpeed = 1;
    this.lastTickWallMs = performance.now();
    this.startLivePoll();
    this.notifyModeChange();
  }

  /** live → playback 戻り。speed は呼び出し側で設定する想定。 */
  exitLive(): void {
    if (this.mode !== 'live') return;
    this.stopLivePoll();
    this.mode = this.cursorMs === null ? 'idle' : 'playback';
    this.notifyModeChange();
  }

  async seek(atMs: number): Promise<void> {
    // seek は live を解除する (fast-forward でなく時刻指定なので)
    const wasLive = this.mode === 'live';
    if (wasLive) this.stopLivePoll();
    this.options.onSeek();
    this.buffer = [];
    if (this.firstAt !== null && this.firstAt < atMs) {
      await this.fetchHistorical(this.firstAt, atMs);
      this.options.onSilentBegin();
      try {
        while (this.buffer.length > 0) {
          const head = this.buffer[0];
          if (!head) break;
          const t = new Date(head.timestamp).getTime();
          if (t > atMs) break;
          this.buffer.shift();
          this.options.dispatch(head, true);
        }
      } finally {
        this.options.onSilentEnd();
      }
      this.fetchedUpto = atMs;
    } else {
      this.fetchedUpto = atMs;
    }
    this.cursorMs = atMs;
    this.lastTickWallMs = performance.now();
    if (wasLive) {
      // seek 完了後は playback として継続
      this.mode = 'playback';
      this.notifyModeChange();
    }
    await this.fetchAhead();
  }

  private async fetchHistorical(fromMs: number, toMs: number): Promise<void> {
    const fromIso = new Date(fromMs).toISOString();
    const toIso = new Date(toMs).toISOString();
    try {
      const res = await authFetch(
        `${API_BASE}/events/range?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
      );
      if (!res.ok) return;
      const parsed: unknown = await res.json();
      if (!Array.isArray(parsed)) return;
      for (const item of parsed) {
        if (isDashboardEvent(item)) this.buffer.push(item);
      }
      this.options.onRangeFetched?.(fromMs, toMs);
      this.buffer.sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      );
    } catch {
      /* noop */
    }
  }

  /** 現在ロード中の日を先頭から再生 (pause 状態)。 */
  async restart(): Promise<void> {
    if (this.loadedDay === null) {
      await this.loadDay(todayJstDateStr(), { autoLive: false });
      return;
    }
    await this.loadDay(this.loadedDay, { autoLive: false });
  }

  async jump(deltaMs: number): Promise<void> {
    if (this.cursorMs === null) {
      await this.restart();
      if (this.cursorMs === null) return;
    }
    await this.seek(this.cursorMs + deltaMs);
  }

  setSpeed(speed: number): void {
    if (this.mode === 'live') this.exitLive();
    this.speed = speed;
    if (this.cursorMs !== null) this.mode = 'playback';
    state.engineSpeed = speed === 0 ? null : speed;
    this.lastTickWallMs = performance.now();
    this.notifyModeChange();
  }

  private notifyModeChange(): void {
    const cb = this.options.onModeChange;
    if (cb) cb(this.mode);
  }

  private async fetchAhead(): Promise<void> {
    if (this.cursorMs === null) return;
    const target = Math.min(
      this.cursorMs + this.options.bucketVirtualMs * 6,
      this.lastAt ?? Number.POSITIVE_INFINITY,
    );
    const fetchedUpto = this.fetchedUpto;
    if (fetchedUpto !== null && fetchedUpto >= target) return;
    const fromMs = fetchedUpto === null ? this.cursorMs : fetchedUpto;
    if (target <= fromMs) return;
    await this.fetchAndAppend(fromMs, target);
    this.fetchedUpto = target;
  }

  private async fetchAndAppend(fromMs: number, toMs: number): Promise<void> {
    const fromIso = new Date(fromMs).toISOString();
    const toIso = new Date(toMs).toISOString();
    try {
      const res = await authFetch(
        `${API_BASE}/events/range?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
      );
      if (!res.ok) return;
      const parsed: unknown = await res.json();
      if (!Array.isArray(parsed)) return;
      for (const item of parsed) {
        if (isDashboardEvent(item)) this.buffer.push(item);
      }
      this.options.onRangeFetched?.(fromMs, toMs);
      this.buffer.sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      );
    } catch {
      /* noop */
    }
  }

  private startLivePoll(): void {
    if (this.livePollHandle !== null) return;
    void this.fetchLive();
    this.livePollHandle = window.setInterval(() => {
      void this.fetchLive();
    }, LIVE_POLL_MS);
  }

  private stopLivePoll(): void {
    if (this.livePollHandle !== null) {
      window.clearInterval(this.livePollHandle);
      this.livePollHandle = null;
    }
  }

  /** live モードで定期的に [fetchedUpto, now] を取りに行く。 */
  private async fetchLive(): Promise<void> {
    if (this.mode !== 'live') return;
    if (this.firstAt === null) return;
    const fromMs = this.fetchedUpto ?? this.cursorMs ?? this.firstAt;
    // raw_data.uploaded_at / job.updated_at は書き込み即時に readonly でも見えるので、
    // 実時間 (Date.now()) まで取りに行ってよい。
    const toMs = Math.max(fromMs + 1, Date.now());
    if (toMs <= fromMs) return;
    await this.fetchAndAppend(fromMs, toMs);
    if (this.mode !== 'live') return;
    this.fetchedUpto = toMs;
  }

  private tick(): void {
    if (this.cursorMs === null) {
      this.lastTickWallMs = performance.now();
      return;
    }
    const wallNow = performance.now();
    const dt = this.lastTickWallMs === null ? 0 : wallNow - this.lastTickWallMs;
    this.lastTickWallMs = wallNow;

    if (this.mode === 'live') {
      // cursor は常に live edge に貼り付く
      this.cursorMs = Date.now() - LIVE_DELAY_MS;
    } else if (this.mode === 'playback' && this.speed > 0) {
      this.cursorMs += dt * this.speed;
      // live edge に到達したら自動切替
      if (this.canGoLiveNow()) {
        const liveEdge = Date.now() - LIVE_DELAY_MS;
        if (this.cursorMs >= liveEdge) {
          this.cursorMs = liveEdge;
          this.speed = 1;
          this.mode = 'live';
          state.engineSpeed = 1;
          this.startLivePoll();
          this.notifyModeChange();
        }
      } else if (this.lastAt !== null && this.cursorMs >= this.lastAt) {
        // 過去日の終端で停止
        this.cursorMs = this.lastAt;
        this.speed = 0;
        state.engineSpeed = null;
        this.notifyModeChange();
      }
    } else {
      return;
    }

    while (this.buffer.length > 0) {
      const head = this.buffer[0];
      if (!head) break;
      const at = new Date(head.timestamp).getTime();
      if (at > this.cursorMs) break;
      this.buffer.shift();
      this.options.dispatch(head, false);
    }

    // playback では先読み。live は別タイマー (startLivePoll) でやる。
    if (this.mode === 'playback') {
      const upto = this.fetchedUpto;
      if (
        !this.fetchInFlight &&
        upto !== null &&
        upto - this.cursorMs < this.options.bucketVirtualMs * 2
      ) {
        this.fetchInFlight = true;
        this.fetchAhead().finally(() => {
          this.fetchInFlight = false;
        });
      }
    }
  }
}
